import React, { useState, useRef, useCallback, useEffect } from 'react';
import { authApi } from '../firebase/config.js';
import { ENV } from '../config/env.js';
import ContentRenderer from './ContentRenderer.jsx';

const API_BASE_URL = ENV.apiBaseUrl;

function fuzzyScore(correct, user) {
    const norm = s => String(s || '').toLowerCase().trim().replace(/[^\w\s]/g, '');
    const c = norm(correct), u = norm(user);
    if (!u) return 0;
    if (c === u) return 100;
    if (c.includes(u) || u.includes(c)) return Math.max(55, 100 - Math.abs(c.length - u.length) * 3);
    const cWords = new Set(c.split(/\s+/).filter(Boolean));
    const hits = u.split(/\s+/).filter(w => w && cWords.has(w)).length;
    return Math.round((hits / Math.max(cWords.size, 1)) * 100);
}

const gradeLabel = score =>
    score >= 80 ? { label: 'Correct', cls: 'text-emerald-300', bg: 'border-emerald-500/30 bg-emerald-500/8' }
    : score >= 50 ? { label: 'Almost', cls: 'text-amber-300', bg: 'border-amber-500/30 bg-amber-500/8' }
    : { label: 'Incorrect', cls: 'text-rose-300', bg: 'border-rose-500/30 bg-rose-500/8' };

export default function VoiceAnswerMode({ folder, onClose }) {
    const [queue] = useState(() => [...(folder.cards || [])].sort(() => Math.random() - 0.5));
    const [index, setIndex] = useState(0);
    const [transcript, setTranscript] = useState('');
    const [listening, setListening] = useState(false);
    const [result, setResult] = useState(null);
    const [grading, setGrading] = useState(false);
    const [history, setHistory] = useState([]);
    const [done, setDone] = useState(false);
    const [supported, setSupported] = useState(true);
    const recognitionRef = useRef(null);

    const card = queue[index];
    const correct = history.filter(h => h.score >= 80).length;

    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { setSupported(false); return; }
        const rec = new SR();
        rec.continuous = false;
        rec.interimResults = true;
        rec.lang = 'en-US';
        rec.onresult = (e) => {
            const t = Array.from(e.results).map(r => r[0].transcript).join(' ');
            setTranscript(t);
        };
        rec.onend = () => setListening(false);
        rec.onerror = () => setListening(false);
        recognitionRef.current = rec;
        return () => { try { rec.abort(); } catch {} };
    }, []);

    const toggleListen = () => {
        if (!recognitionRef.current) return;
        if (listening) {
            recognitionRef.current.stop();
        } else {
            setTranscript('');
            setResult(null);
            recognitionRef.current.start();
            setListening(true);
        }
    };

    const handleSubmit = useCallback(async () => {
        if (!transcript.trim() || !card || grading) return;
        setGrading(true);
        let score = fuzzyScore(card.answer, transcript);
        try {
            const data = await authApi(`${API_BASE_URL}/score-answer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ correctAnswer: String(card.answer || ''), userAnswer: transcript }),
            });
            score = Number(data.score ?? score);
        } catch { /* use local score */ }
        setResult({ score, correctAnswer: card.answer });
        setHistory(prev => [...prev, { card, score, userAnswer: transcript }]);
        setGrading(false);
    }, [transcript, card, grading]);

    const handleNext = () => {
        if (index + 1 >= queue.length) { setDone(true); return; }
        setIndex(i => i + 1);
        setTranscript('');
        setResult(null);
    };

    // Read question aloud
    const speakQuestion = () => {
        if (!card) return;
        const utt = new SpeechSynthesisUtterance(card.question);
        utt.rate = 0.9;
        window.speechSynthesis.speak(utt);
    };

    if (done) {
        const total = history.length;
        return (
            <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-slate-900/95 shadow-2xl p-8 text-center">
                    <div className="text-5xl mb-4">🎤</div>
                    <h2 className="text-2xl font-bold text-white mb-2">Session Complete!</h2>
                    <p className="text-slate-400 mb-1">{correct} of {total} spoken answers correct</p>
                    <div className="h-2.5 rounded-full bg-white/10 my-5 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all"
                            style={{ width: `${total > 0 ? (correct / total) * 100 : 0}%` }} />
                    </div>
                    <button onClick={onClose}
                        className="rounded-full px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 transition">
                        Done
                    </button>
                </div>
            </div>
        );
    }

    if (!supported) return (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900/95 p-8 text-center">
                <div className="text-4xl mb-4">🎙️</div>
                <h2 className="text-lg font-semibold text-white mb-2">Voice not supported</h2>
                <p className="text-slate-400 text-sm mb-4">Your browser doesn't support the Web Speech API. Try Chrome or Edge.</p>
                <button onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-slate-300 bg-white/5 ring-1 ring-white/10 hover:bg-white/10 transition">
                    Close
                </button>
            </div>
        </div>
    );

    if (!card) return null;
    const g = result ? gradeLabel(result.score) : null;

    return (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex flex-col px-2 py-3 sm:px-4 sm:py-5">
            <div className="mx-auto w-full max-w-xl rounded-2xl border border-white/10 bg-slate-900/95 shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '100%' }}>
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b border-white/8">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-white">Voice Mode</span>
                        <span className="text-xs text-slate-400">— {folder.name}</span>
                        <span className="text-xs text-teal-300 bg-teal-500/15 rounded-full px-2 py-0.5 tabular-nums">{correct}/{history.length}</span>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                {/* Progress */}
                <div className="h-1 bg-white/5 shrink-0">
                    <div className="h-full bg-gradient-to-r from-teal-400 to-emerald-400 transition-all duration-300"
                        style={{ width: `${queue.length > 0 ? (index / queue.length) * 100 : 0}%` }} />
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
                    <p className="text-xs text-slate-500 tabular-nums">{index + 1} of {queue.length}</p>

                    {/* Question */}
                    <div className="rounded-2xl border border-teal-500/20 bg-teal-500/5 p-5">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-400/70 mb-2">Question</p>
                                <p className="text-white text-base leading-relaxed"><ContentRenderer content={card.question} /></p>
                            </div>
                            <button onClick={speakQuestion} title="Read question aloud"
                                className="shrink-0 rounded-full p-2 bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition mt-1">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Mic control */}
                    {!result && (
                        <div className="flex flex-col items-center gap-4">
                            <button
                                onClick={toggleListen}
                                className={`relative h-20 w-20 rounded-full transition-all duration-200 flex items-center justify-center ${
                                    listening
                                        ? 'bg-rose-600 hover:bg-rose-500 shadow-lg shadow-rose-500/30 scale-110'
                                        : 'bg-teal-600 hover:bg-teal-500 shadow-lg shadow-teal-500/20'
                                }`}
                            >
                                {listening && (
                                    <span className="absolute inset-0 rounded-full bg-rose-500/30 animate-ping" />
                                )}
                                <svg className="h-8 w-8 text-white" fill={listening ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                            </button>
                            <p className="text-sm text-slate-400">{listening ? 'Listening… speak your answer' : 'Tap mic to start'}</p>

                            {transcript && (
                                <div className="w-full rounded-xl border border-white/10 bg-slate-800/50 px-4 py-3">
                                    <p className="text-xs text-slate-500 mb-1">Heard:</p>
                                    <p className="text-sm text-white">{transcript}</p>
                                </div>
                            )}

                            {transcript && !listening && (
                                <button onClick={handleSubmit} disabled={grading}
                                    className="w-full rounded-xl py-3 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 disabled:opacity-40 transition">
                                    {grading ? 'Checking…' : 'Submit Answer'}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Result */}
                    {result && g && (
                        <div className={`rounded-2xl border p-5 space-y-3 ${g.bg}`}>
                            <div className="flex items-center gap-3">
                                <span className={`text-2xl font-bold tabular-nums ${g.cls}`}>{result.score}%</span>
                                <span className={`text-sm font-semibold ${g.cls}`}>{g.label}</span>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">You said</p>
                                <p className="text-sm text-slate-300">{transcript}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Correct answer</p>
                                <p className="text-sm text-white"><ContentRenderer content={result.correctAnswer} /></p>
                            </div>
                            <button onClick={handleNext}
                                className="w-full rounded-xl py-2.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-500 transition">
                                {index + 1 >= queue.length ? 'Finish' : 'Next →'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
